import { useMemo } from 'react';
import { enqueueSnackbar } from 'notistack';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Autocomplete, TextField, Button, Tooltip, Stack } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PropTypes from 'prop-types';
import AwaitButton from '../../../../reusable-components/await-button/AwaitButton'
import { vendorPriceAccountForm } from '../../../../api/vendors';
import CloseIconButton from '../../../../reusable-components/basic-buttons/CloseIconButton';

NewPriceAccountDialog.propTypes = {
  setIsPriceAccountDialogOpen: PropTypes.func,
  isPriceAccountDialogOpen: PropTypes.bool,
  onSuccess: PropTypes.func,
  vendorOptions: PropTypes.array,
  value: PropTypes.object,
  setValue: PropTypes.func,
  priceAccounts: PropTypes.array
};

export default function NewPriceAccountDialog({ setIsPriceAccountDialogOpen, isPriceAccountDialogOpen, onSuccess, vendorOptions, value, setValue, priceAccounts }) {

  const handleClose = () => {
    setIsPriceAccountDialogOpen(false);
    setValue({});
  }


  const isDuplicateAccountNumber = useMemo(() => {
    if (!value.vendorId || !value.accountNumber?.trim()) return false;
    return (priceAccounts || []).some((account) =>
      account.id !== value.id &&
      account.vendorId === value.vendorId &&
      account.accountNumber?.trim().toLowerCase() === value.accountNumber.trim().toLowerCase()
    );
  }, [priceAccounts, value.vendorId, value.accountNumber, value.id]);

  const handleSave = async () => {
    await vendorPriceAccountForm(value)
      .then(() => {
        onSuccess();
        handleClose();
        enqueueSnackbar(`Vendor price account ${(!value.id ? "added" : "updated")}`, { variant: 'success' })
      })
      .catch(() => enqueueSnackbar(`Failed to ${(!value.id ? "add new" : "update")} vendor price account`, { variant: 'error' }));  
  }

  return <Dialog open={isPriceAccountDialogOpen} onClose={(event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
    handleClose();
  }} maxWidth="sm" fullWidth>
    <DialogTitle sx={{ textAlign: 'center' }}>
      {value.id ? 'Edit' : 'New'} Vendor Price Account <CloseIconButton onClick={handleClose} />
    </DialogTitle>
    <DialogContent>
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Vendor*
          </Typography>
          <Autocomplete
            fullWidth
            value={value.vendorId ? vendorOptions.find(option => option.id === value.vendorId) : null}
            getOptionLabel={(option) => option.label}
            onChange={(_, newValue) => setValue(prev => ({ ...prev, vendorId: newValue?.id }))}
            options={vendorOptions}
            isOptionEqualToValue={(option, selected) => option.id === selected.id}
            renderInput={(params) => <TextField {...params} label="Search or select" autoComplete="off" />}
            sx={{
              mt: 1,
              '& legend': { display: 'none' },
              '& fieldset': { top: 0 },
              '& .MuiFormLabel-root ': { display: 'none' },
            }}
            size="small"
          />
        </Box>
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Price Account Number*
          </Typography>
          <TextField
            fullWidth
            value={value.accountNumber || ''}
            onChange={(event) => setValue(prev => ({ ...prev, accountNumber: event.target.value }))}
            size="small"
            error={isDuplicateAccountNumber}
            helperText={isDuplicateAccountNumber ? 'This vendor already has a price account with this number' : ''}
          />
        </Box>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <Typography variant="subtitle1">
              Price Account Description
            </Typography>
            <Tooltip title="A descriptive name for this price account, used to distinguish pricing tiers." placement="top" arrow>
              <InfoOutlinedIcon sx={{ fontSize: 16, color: 'info.main' }} />
            </Tooltip>
          </Box>
          <TextField
            value={value.description || ''}
            fullWidth
            onChange={(event) => setValue(prev => ({ ...prev, description: event.target.value }))}
            size="small"
          />
        </Box>
      </Stack>
    </DialogContent>
    <DialogActions>
      <Button variant="outlined" color="error" size="medium" sx={{ width: 150, mr: 2 }} onClick={handleClose}>
        Cancel
      </Button>
      <AwaitButton
        variant="contained"
        color="primary"
        size="medium"
        disabled={!value.accountNumber?.trim() || !value.vendorId || isDuplicateAccountNumber}
        sx={{ width: 150 }}
        onClick={handleSave}
      >
        Save
      </AwaitButton>
    </DialogActions>
  </Dialog>;
}